/**
 * API module para consulta processual (autos digitais).
 *
 * Endpoints do microserviço processual — consulta de processos reais
 * nos tribunais via certificado digital do usuário.
 */
import { api } from './client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Grau {
  sigla: string;
  nome: string;
  numero: number;
}

export interface Classe {
  codigo: number | null;
  descricao: string;
}

export interface Assunto {
  codigo: number;
  descricao: string;
  hierarquia: string | null;
}

export interface TribunalProcessual {
  sigla: string;
  nome: string;
  segmento: string;
  jtr: string;
}

// Os objetos de distribuição são serializados pelo ALIAS do schema Pydantic
// (camelCase), como `Parte` — `backend/app/schemas/processual.py`.
export interface JurisdicaoLocal {
  id: number;
  idOrigem: string | null;
  nome: string | null;
}

export interface OrgaoJulgadorLocal {
  id: number;
  idOrigem: string | null;
  nome: string;
  jurisdicaoLocal: JurisdicaoLocal | null;
}

export interface OrgaoJulgador {
  id: number | null;
  nome: string | null;
}

export interface Distribuicao {
  dataHora: string;
  orgaoJulgador: OrgaoJulgador[];
  orgaoJulgadorLocal: OrgaoJulgadorLocal | null;
}

export interface DocumentoPrincipal {
  numero: string | number | null;
  tipo: string | null;
}

export interface Representante {
  tipoRepresentacao: string | null;
  nome: string;
  cadastroReceitaFederal: DocumentoPrincipal[] | null;
}

export interface Parte {
  polo: string;
  nome: string;
  documentosPrincipais: DocumentoPrincipal[] | null;
  assistenciaJudiciariaGratuita: boolean | null;
  sigilosa: boolean;
  /** Nome e documento vieram ocultados pela fonte (Res. 121 ou sigilo): não dá para saber quem é.
   *  Opcional: snapshots em cache anteriores ao campo não o trazem. */
  oculta?: boolean;
  representantes: Representante[] | null;
  // Enrichment fields (filled by backend party_match / manual link merge)
  cliente_id: string | null;
  cliente_nome: string | null;
  cliente_cpf: string | null;
}

/** De onde veio o item: `TRIBUNAL` = conferência com o tribunal (o Data Lake ainda não tem). */
export type OrigemItemAutos = 'DATALAKE' | 'TRIBUNAL';

export interface Movimento {
  dataHora: string;
  descricao: string;
  /** Opcional: snapshots em cache anteriores ao campo não o trazem. */
  origem?: OrigemItemAutos | null;
}

export interface TipoDocumento {
  nome: string;
  codigo: number | null;
}

export interface ArquivoInfo {
  tipo: string;
  quantidade_paginas: number;
  tamanho: number;
  tamanho_texto: number;
}

export type StatusDownload = 'BAIXADO' | 'PENDENTE' | 'FALHA';

export interface DocumentoJudicialOut {
  sequencia: number;
  data_hora_juntada: string;
  uuid: string;
  id_origem: string | null;
  nome: string;
  nivel_sigilo: string | number;
  tipo: TipoDocumento;
  arquivo: ArquivoInfo | null;
  status_download: StatusDownload;
  tamanho_bytes: number | null;
  mime_type: string | null;
  /** Hash do binário baixado (nulo enquanto não baixado). Opcional: snapshots em
   *  cache anteriores ao campo não o trazem. */
  content_hash?: string | null;
  /** Opcional: snapshots em cache anteriores ao campo não o trazem. */
  origem?: OrigemItemAutos | null;
}

export interface ProcessoRelacionado {
  numero_processo: string;
  href: string | null;
  tipo_relacao: string;
  ativo: boolean;
}

export interface TramitacaoOut {
  id_origem: string;
  /**
   * Id da fonte de dados CODEX — usado pelo wizard de peticionamento para
   * pré-selecionar a tramitação via query param. Pode ser null em processos
   * legados sem materialização no Data Lake.
   */
  id_fonte_dados_codex: string | null;
  data_hora_ajuizamento: string;
  instancia: string | null;
  grau: Grau;
  valor_acao: number | null;
  classe: Classe[];
  assunto: Assunto[];
  tribunal: TribunalProcessual | null;
  distribuicoes: Distribuicao[];
  movimentos: Movimento[];
  partes: Parte[];
  documentos: DocumentoJudicialOut[];
  permite_peticionar: boolean;
  processos_relacionados: ProcessoRelacionado[];
  ativo: boolean;
  sistema_processual: string | null;
}

export interface CacheInfo {
  /**
   * Origem da resposta. A partir da Fase 4 do plano SWR, `redis` foi removido
   * — autenticador é fonte da verdade e tem cache próprio.
   */
  source: 'live' | 'not_modified';
  /**
   * Timestamp ISO-8601 do snapshot no Data Lake CNJ (campo `atualizadoEm`
   * propagado pelo autenticador). Pode ser null em respostas legadas. A UI
   * usa esse valor para mostrar "Sincronizado há X" no strip dos autos.
   */
  atualizado_em: string | null;
  /**
   * Classificação do processo segundo o autenticador. Espelha o header
   * `X-Tipo-Acesso`. Driva o cadeado de "Sigiloso" e o EmptyState de
   * `TRIBUNAL_NAO_INTEGRADO_SIGILOSO`.
   */
  tipo_acesso: string | null;
  /**
   * Sistema processual de origem (PJE | EPROC | SAJ | PROJUDI | OUTROS).
   * Espelha `X-Sistema-Processual`. Driva o banner "Tribunal não-PJe".
   */
  sistema_processual: string | null;
  /** Idade do snapshot upstream em segundos. Espelha o header `Age`. */
  age_seconds: number | null;
  /**
   * No processo de um colega, o nome do dono quando os autos vieram com o certificado
   * dele (Bloco 3b); nulo no próprio e na visão pública. Ausente em respostas antigas.
   */
  certificado_de?: string | null;
  /** No processo de um colega: os autos vieram com o certificado de quem lê (o dono não tem um que sirva). */
  certificado_proprio?: boolean;
  /** A consulta foi pela visão pública (Res. 121, certificado de serviço). */
  visao_publica?: boolean;
}

/**
 * Processo relacionado já normalizado pelo backend: sem autorreferência, sem paradigma de tema,
 * sem repetição entre tramitações. `no_acervo` diz se o usuário já tem o processo cadastrado.
 */
export interface ProcessoRelacionadoOut {
  numero_processo: string;
  numero_formatado: string;
  tipo_relacao: string;
  classe: string | null;
  ativo: boolean;
  no_acervo: boolean;
}

export interface ProcessoJudicialResponse {
  numero_processo: string;
  sigla_tribunal: string;
  nivel_sigilo: number;
  tramitacao_atual: TramitacaoOut;
  tramitacoes: TramitacaoOut[];
  /** Ausente em snapshot offline anterior ao campo — tratar como lista vazia. */
  processos_relacionados?: ProcessoRelacionadoOut[];
  _cache: CacheInfo;
}

export interface DocumentoBinarioResponse {
  url: string;
  content_type: string;
  valido_ate: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Agrupa partes por polo, ordenando ATIVO > PASSIVO > demais. */
export function groupPartesByPolo(partes: Parte[]): { polo: string; label: string; partes: Parte[] }[] {
  const map = new Map<string, Parte[]>();
  for (const p of partes) {
    const polo = p.polo || 'OUTROS';
    const list = map.get(polo) ?? [];
    list.push(p);
    map.set(polo, list);
  }

  const POLO_ORDER = ['ATIVO', 'PASSIVO'];
  const POLO_LABELS: Record<string, string> = {
    ATIVO: 'Polo Ativo',
    PASSIVO: 'Polo Passivo',
    OUTROS_PARTICIPANTES: 'Outros participantes',
    OUTROS: 'Outros',
    TERCEIRO: 'Terceiros',
  };
  // Polo que o tribunal manda e não está na lista: legível, nunca o nome interno
  // ("OUTROS_PARTICIPANTES" aparecia assim na ficha do processo).
  const rotulo = (polo: string) => {
    const t = polo.replace(/_/g, ' ').toLowerCase();
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  return [...map.entries()]
    .sort(([a], [b]) => {
      const ia = POLO_ORDER.indexOf(a);
      const ib = POLO_ORDER.indexOf(b);
      if (ia >= 0 && ib >= 0) return ia - ib;
      if (ia >= 0) return -1;
      if (ib >= 0) return 1;
      return a.localeCompare(b);
    })
    .map(([polo, partes]) => ({
      polo,
      label: POLO_LABELS[polo] ?? rotulo(polo),
      partes,
    }));
}

/** Conta partes por polo (para exibição resumida). */
export function countPartesByPolo(partes: Parte[]): { ativo: number; passivo: number; outros: number } {
  let ativo = 0, passivo = 0, outros = 0;
  for (const p of partes) {
    if (p.polo === 'ATIVO') ativo++;
    else if (p.polo === 'PASSIVO') passivo++;
    else outros++;
  }
  return { ativo, passivo, outros };
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

// Timeout enxuto: o backend devolve 202 imediato quando precisa disparar carga
// (não bloqueia mais síncrono no Data Lake) e, no pior caso, fica preso ~15s
// (compatível com `autenticador_timeout_consulta_read=15s`). 30s deixa margem
// folgada para esse caminho sem deixar o axios pendurado por minutos.
const PROCESSUAL_CONSULTA_TIMEOUT_MS = 30_000;

export interface FetchProcessoOptions {
  /**
   * HTTP-date (RFC 7231) recebido no `Last-Modified` da resposta anterior.
   * Quando passado, o backend pode responder 304 Not Modified, sinalizado
   * por `notModified: true` no resultado.
   */
  ifModifiedSince?: string | null;
  /**
   * Força bypass do cache Redis no backend (e do dedup local do autenticador).
   * Usado pelo botão "Atualizar agora" no header dos autos.
   */
  refresh?: boolean;
}

export interface FetchProcessoResult {
  /** Dados do processo. Null em 304 Not Modified ou 202 Accepted. */
  data: ProcessoJudicialResponse | null;
  /** Valor cru do header `Last-Modified` retornado pelo backend. */
  lastModified: string | null;
  /** True quando o servidor respondeu 304 (snapshot anterior continua válido). */
  notModified: boolean;
  /** True quando o servidor respondeu 202 — carga inicial em andamento. */
  accepted: boolean;
  /** ID do job de carga (presente em 202 ou em 200 com auto-trigger). */
  jobId: string | null;
  /** Modo de carga decidido pelo autenticador (DATALAKE_PJE | PJE_ONLY | ...). */
  modo: string | null;
}

interface AcceptedBody {
  job_id: string | null;
  modo: string | null;
  status: string;
}

export async function fetchProcessoJudicial(
  numero: string,
  options: FetchProcessoOptions = {},
): Promise<FetchProcessoResult> {
  const headers: Record<string, string> = {};
  if (options.ifModifiedSince) {
    headers['If-Modified-Since'] = options.ifModifiedSince;
  }
  const params: Record<string, string> = {};
  if (options.refresh) {
    // ?refresh=true é deprecated mas mantido para compat — backend ignora.
    params.refresh = 'true';
  }

  const response = await api.get<ProcessoJudicialResponse | AcceptedBody>(
    `/processual/processos/${encodeURIComponent(numero)}`,
    {
      timeout: PROCESSUAL_CONSULTA_TIMEOUT_MS,
      headers,
      params,
      // 304 e 202 são respostas válidas neste fluxo; o axios precisa não
      // derrubar nenhuma delas como erro.
      validateStatus: (s) => s === 304 || s === 202 || (s >= 200 && s < 300),
    },
  );

  const lastModified =
    (response.headers?.['last-modified'] as string | undefined) ??
    (response.headers?.['Last-Modified'] as string | undefined) ??
    null;

  // Auto-trigger header (200 OK que disparou carga em background)
  const headerJobId =
    (response.headers?.['x-refresh-job-id'] as string | undefined) ??
    (response.headers?.['X-Refresh-Job-Id'] as string | undefined) ??
    null;

  if (response.status === 304) {
    return { data: null, lastModified, notModified: true, accepted: false, jobId: null, modo: null };
  }

  if (response.status === 202) {
    const body = response.data as AcceptedBody;
    return {
      data: null,
      lastModified: null,
      notModified: false,
      accepted: true,
      jobId: body.job_id ?? null,
      modo: body.modo ?? null,
    };
  }

  return {
    data: response.data as ProcessoJudicialResponse,
    lastModified,
    notModified: false,
    accepted: false,
    jobId: headerJobId,
    modo: null,
  };
}

export interface RefreshProcessoResponse {
  job_id: string;
  status: string;
  ja_existia: boolean;
  modo: string;
}

/**
 * Dispara carga assíncrona do processo (válvula manual do botão "Atualizar
 * agora"). 202 com body snake_case. Sem cooldown — backend dedupa via
 * `enqueueOrFindActive`.
 */
export async function postRefreshProcesso(numero: string): Promise<RefreshProcessoResponse> {
  const { data } = await api.post<RefreshProcessoResponse>(
    `/processual/processos/${encodeURIComponent(numero)}/refresh`,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Conferência com o tribunal (PLANO_SINCRONIZACAO_TRIBUNAL)
// ---------------------------------------------------------------------------

export type GatilhoSincronizacao = 'ABERTURA_AUTOS' | 'BOTAO_MANUAL';

export type StatusSincronizacao =
  | 'ENFILEIRADO'
  | 'EM_EXECUCAO'
  | 'CONCLUIDO'
  | 'CONCLUIDO_PARCIAL'
  | 'FALHA'
  | 'NAO_ELEGIVEL';

/** Última conferência do processo. `job_id`/`status` nulos quando nunca houve. */
export interface SincronizacaoTribunalEstado {
  job_id: string | null;
  status: StatusSincronizacao | null;
  gatilho: GatilhoSincronizacao | null;
  /** Alguma tramitação do processo tem a conferência habilitada. */
  elegivel: boolean;
  /** Pedida por quem está olhando. A conferência é uma por processo: falha alheia não é falha minha. */
  propria: boolean;
  movimentos_novos: number | null;
  documentos_novos: number | null;
  codigo_erro: string | null;
  criado_em: string | null;
  iniciado_em: string | null;
  finalizado_em: string | null;
}

export interface SincronizacaoTribunalPedido {
  job_id: string | null;
  status: StatusSincronizacao;
  ja_existia: boolean;
  /** Devolveu a última conferência, feita há pouco, sem ir ao tribunal de novo. */
  recente: boolean;
  elegivel: boolean;
  ultima_conferencia_em: string | null;
}

export async function getSincronizacaoTribunal(numero: string): Promise<SincronizacaoTribunalEstado> {
  const { data } = await api.get<SincronizacaoTribunalEstado>(
    `/processual/processos/${encodeURIComponent(numero)}/sincronizacao-tribunal`,
  );
  return data;
}

export async function postSincronizacaoTribunal(
  numero: string,
  gatilho: GatilhoSincronizacao,
): Promise<SincronizacaoTribunalPedido> {
  const { data } = await api.post<SincronizacaoTribunalPedido>(
    `/processual/processos/${encodeURIComponent(numero)}/sincronizacao-tribunal`,
    { gatilho },
  );
  return data;
}

// Timeout do endpoint de binário: o autenticador agora baixa síncrono
// (1 tentativa de presigned URL, fallback 1 tentativa em stream). Pode demorar
// até ~30s no pior caso, mas sempre resolve em sucesso ou erro real. 45s deixa
// margem folgada para esse caminho sem deixar o axios pendurado.
const DOCUMENTO_BINARIO_TIMEOUT_MS = 45_000;

/**
 * `alta` = documento que o usuário clicou; `baixa` = vizinho do read-ahead de
 * adjacência. O backend segrega o budget de rate limit por este valor
 * (`PROCESSUAL_RL_DOCUMENTO_LIMIT` vs `PROCESSUAL_RL_DOCUMENTO_PREFETCH_LIMIT`)
 * para que a rajada de prefetch não consuma o orçamento do clique real.
 *
 * Efeito colateral a ter em mente: declarar a prioridade **triplica o teto por
 * usuário** contra o Data Lake. Antes prefetch e clique dividiam os 300/h de
 * `PROCESSUAL_RL_DOCUMENTO_LIMIT`; agora são 300 (clique) + 600 (prefetch) =
 * 900/h. É o que protege o orçamento do clique — o ponto da mudança —, mas é
 * neutro-a-pior para a contenção do upstream, que foi o gargalo do incidente
 * da issue #68. Não morde no tráfego atual (o pico era 4–18 req/min); se um dia
 * morder, o ajuste é em `PROCESSUAL_RL_DOCUMENTO_PREFETCH_LIMIT`.
 */
export type PrioridadeDocumentoBinario = 'alta' | 'baixa';

export interface FetchDocumentoBinarioOptions {
  /**
   * AbortSignal opcional para cancelar a requisição em curso. Usado pelo
   * prefetch de adjacência: quando o usuário troca de processo/documento, os
   * prefetches anteriores são abortados via `controller.abort()` para liberar
   * o rate limit do autenticador. Em caso de cancelamento, o axios lança
   * `CanceledError` — o caller pode pegar com `axios.isCancel(err)`.
   */
  signal?: AbortSignal;
  /**
   * Prioridade da requisição. Omitir vale como `alta` — o que só é correto
   * para o clique do usuário. Todo caminho de read-ahead PRECISA mandar
   * `baixa`: sem isso o prefetch debita o orçamento do clique e nenhum
   * log/auditoria/access log distingue tráfego de fundo do interativo (foi o
   * que escondeu a origem do pico de p95 de 2026-09-09, issue #68).
   */
  prioridade?: PrioridadeDocumentoBinario;
}

/**
 * Busca o binário de um documento. O backend sempre devolve 200 com a presigned
 * URL pronta (o autenticador faz download síncrono internamente).
 */
export async function fetchDocumentoBinario(
  numero: string,
  uuid: string,
  options: FetchDocumentoBinarioOptions = {},
): Promise<DocumentoBinarioResponse> {
  const { data } = await api.get<DocumentoBinarioResponse>(
    `/processual/processos/${encodeURIComponent(numero)}/documentos/${encodeURIComponent(uuid)}/binario`,
    {
      timeout: DOCUMENTO_BINARIO_TIMEOUT_MS,
      signal: options.signal,
      // O param vai SEMPRE, inclusive `alta`. Omiti-lo no clique tornaria a
      // identificação assimétrica: `?prioridade=baixa` provaria prefetch, mas
      // a ausência de query string não provaria clique — poderia ser um clique
      // do bundle novo ou qualquer requisição de um bundle PWA antigo ainda em
      // cache. Uma triagem futura que repetisse o método da issue #68 ("N
      // requisições sem query string ⇒ o web não declara prioridade") erraria
      // durante toda a janela de rollout. Não há cache de axios a fragmentar:
      // `api/client.ts` é um `axios.create` cru e o service worker roteia
      // `/api/*` por NetworkOnly.
      params: { prioridade: options.prioridade ?? 'alta' },
    },
  );
  return data;
}

/** 202 do binário: o Data Lake não entregou e a conferência `job_id` vai buscar o documento no tribunal. */
export interface DocumentoBuscandoNoTribunalResponse {
  status: 'buscando_no_tribunal';
  job_id: string;
}

export type DocumentoBinarioOuTribunal =
  | { tipo: 'documento'; documento: DocumentoBinarioResponse }
  | { tipo: 'tribunal'; jobId: string };

/**
 * O clique do usuário: como `fetchDocumentoBinario` (prioridade `alta`), mas se o Data Lake não
 * entregar o arquivo e o tribunal tiver a conferência ligada, o backend responde 202 e o documento
 * passa a ser buscado no PJe (`tipo: 'tribunal'`). O read-ahead nunca usa esta função.
 */
export async function fetchDocumentoBinarioOuTribunal(
  numero: string,
  uuid: string,
  options: { signal?: AbortSignal } = {},
): Promise<DocumentoBinarioOuTribunal> {
  const resp = await api.get<DocumentoBinarioResponse | DocumentoBuscandoNoTribunalResponse>(
    `/processual/processos/${encodeURIComponent(numero)}/documentos/${encodeURIComponent(uuid)}/binario`,
    {
      timeout: DOCUMENTO_BINARIO_TIMEOUT_MS,
      signal: options.signal,
      params: { prioridade: 'alta', fallback_tribunal: true },
    },
  );
  if (resp.status === 202) {
    return { tipo: 'tribunal', jobId: String((resp.data as DocumentoBuscandoNoTribunalResponse).job_id) };
  }
  return { tipo: 'documento', documento: resp.data as DocumentoBinarioResponse };
}

// ---------------------------------------------------------------------------
// Sync Partes (enrichment)
// ---------------------------------------------------------------------------

export interface SyncPartesResponse {
  created: number;
  updated: number;
  linked: number;
  auto_created: number;
}

/**
 * Partes de um processo relacionado. O Data Lake informa de cada relacionado só número, tipo e
 * classe — sem as partes, o número não diz ao advogado de que processo se trata. Buscadas sob
 * demanda, ao abrir a lista. Sigiloso não volta.
 */
export interface CapaRelacionadoOut {
  /** Só dígitos, como `ProcessoRelacionadoOut.numero_processo`. */
  numero_processo: string;
  partes: Parte[];
  /** Ausente em backend anterior ao campo. */
  classe?: string | null;
  em_tramitacao?: boolean;
}

/**
 * Um processo relacionado (docs/PLANO_FAMILIA_DO_PROCESSO.md): lido dos autos (certidões do PJe),
 * declarado pela PDPJ ou incluído pelo usuário ou por um colega de escritório.
 */
export interface VinculoFamiliaOut {
  /** Só dígitos. */
  numero_processo: string;
  numero_formatado: string;
  /** Nulo no principal e no incluído: a capa completa. */
  classe: string | null;
  /** Só nos que a PDPJ declara (INCIDENTAL, CONEXAO…). */
  tipo_relacao: string | null;
  confirmado: boolean;
  forte: boolean;
  /** ISO, da certidão de interposição. */
  protocolado_em: string | null;
  /** Fora na visão pública (Res. 121). */
  protocolado_por: string | null;
  /** De onde o vínculo foi lido — não vai à tela (D10). */
  evidencia: string | null;
  no_acervo: boolean;
  /** O processo aberto, entre os recursos da raiz. */
  este: boolean;
  /** Incluído pelo usuário ou por um colega (D11). */
  informado: boolean;
  /** "você" ou o nome de quem incluiu. */
  incluido_por: string | null;
  pode_retirar: boolean;
}

/** A árvore dos relacionados: parte do principal (``raiz``) quando o aberto é um recurso. */
export interface FamiliaOut {
  numero_processo: string;
  /** A leitura em 2º plano já passou por este processo. */
  lida: boolean;
  raiz: VinculoFamiliaOut | null;
  outros_principais: VinculoFamiliaOut[];
  /** Os recursos da raiz (ou deste, sem raiz), com o aberto entre eles (``este``). */
  recursos: VinculoFamiliaOut[];
  /** Os recursos interpostos no aberto, quando ele não é a raiz. */
  recursos_deste: VinculoFamiliaOut[];
  /** O que a PDPJ declara e o que o usuário ou o escritório incluiu. */
  ligados: VinculoFamiliaOut[];
}

export async function fetchFamilia(numero: string): Promise<FamiliaOut> {
  const { data } = await api.get<FamiliaOut>(`/processual/processos/${encodeURIComponent(numero)}/familia`);
  return data;
}

export async function incluirRelacionado(numero: string, relacionado: string): Promise<void> {
  await api.post(`/processual/processos/${encodeURIComponent(numero)}/relacionados`, { numero: relacionado });
}

export async function retirarRelacionado(numero: string, relacionado: string): Promise<void> {
  await api.delete(
    `/processual/processos/${encodeURIComponent(numero)}/relacionados/${encodeURIComponent(relacionado)}`,
  );
}

export async function fetchCapasRelacionados(numeros: string[]): Promise<CapaRelacionadoOut[]> {
  const { data } = await api.get<CapaRelacionadoOut[]>('/processual/relacionados/capas', {
    params: { numeros: numeros.join(',') },
  });
  return data;
}

export async function syncPartes(numero: string): Promise<SyncPartesResponse> {
  const { data } = await api.post<SyncPartesResponse>(
    `/processual/processos/${encodeURIComponent(numero)}/sync-partes`,
  );
  return data;
}
